/**
 * User-script custom-widget builder — React authoring surface.
 *
 * Lets an operator/power-user assemble a dashboard widget without writing a
 * component: pick a data-source endpoint, write a value expression in the
 * sandboxed language (`custom-widget-script.ts`), choose a presentation
 * format, and see a live preview rendered inside the real `WidgetCard`
 * shell. On save it emits a validated `WidgetDescriptor` + a persistable
 * `CustomWidgetDefinition` (assembly + validation live in the React-free
 * `custom-widget-builder.ts`, which is unit-tested directly).
 *
 * ──────────────────────────────────────────────────────────────────────
 * What ships:
 *
 *   ✅ Full builder form (label / module / permission / endpoint / poll /
 *      script / format / unit / size) with inline, field-level validation.
 *   ✅ Sandboxed script validation + a working preview that evaluates the
 *      expression against *sample JSON the author pastes in* (deterministic,
 *      no backend, no eval — see `evaluateScript`).
 *   ✅ Backend-compatibility classification: the form tells the author whether
 *      the script falls inside the backend `SimpleExpression` sandbox subset
 *      (so it can be persisted + executed server-side) or is preview-only,
 *      reconciling the front-end grammar with the server contract instead of
 *      being a silently divergent second engine.
 *   ✅ Descriptor + definition emission via `onSave`, which the mounting admin
 *      route persists through the widget definitions import endpoint.
 *
 * Preview-only seam (tracked, intentionally NOT faked):
 *
 *   ⛔ Live-data preview: fetching the real `endpointPath` through
 *      `useWidgetData` and previewing against the live response. The hook
 *      exists (`./widget-contract`) but needs a QueryClient + auth context
 *      that only the shell provides, so it is left to the shell-side
 *      integration rather than mocked here.
 *
 * The non-UI logic this scaffold depends on (parser, validator, evaluator,
 * backend-compatibility classifier, descriptor assembly, value formatting) is
 * complete and fully tested.
 * ──────────────────────────────────────────────────────────────────────
 */

import * as React from 'react';
import { Input } from '../primitives/input';
import { Label } from '../primitives/label';
import { Textarea } from '../primitives/textarea';
import { Button } from '../primitives/button';
import { Alert, AlertDescription, AlertTitle } from '../primitives/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/select';
import { WidgetCard } from './widget-card';
import {
  buildCustomWidget,
  formatWidgetValue,
  type CustomWidgetDraft,
  type CustomWidgetFormat,
  type BuildResult,
} from './custom-widget-builder.ts';
import {
  evaluateScript,
  validateScript,
  classifyBackendCompatibility,
  type ValidationIssue,
} from './custom-widget-script.ts';
import type { WidgetDescriptor, WidgetSize } from '@tyndo/types';
import type { CustomWidgetDefinition } from './custom-widget-builder.ts';

const FORMAT_OPTIONS: ReadonlyArray<{ value: CustomWidgetFormat; label: string }> = [
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percent' },
  { value: 'currency', label: 'Currency (USD)' },
  { value: 'duration', label: 'Duration (from seconds)' },
  { value: 'text', label: 'Text' },
];

const SIZE_OPTIONS: ReadonlyArray<{ value: WidgetSize; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const DEFAULT_DRAFT: CustomWidgetDraft = {
  label: '',
  moduleId: 'tyndo.production',
  readPermission: '',
  endpointPath: '',
  valueScript: '',
  format: 'number',
  size: 'small',
};

const DEFAULT_SAMPLE_JSON = '{\n  "value": 42\n}';

export interface CustomWidgetBuilderProps {
  /** Seed the form (e.g. when editing an existing custom widget). */
  initialDraft?: Partial<CustomWidgetDraft>;
  /**
   * Called with the validated descriptor + definition when the user saves a
   * valid widget. The caller persists the definition + registers the widget.
   */
  onSave?: (result: { descriptor: WidgetDescriptor; definition: CustomWidgetDefinition }) => void;
  /** Cancel handler — renders a Cancel button when set. */
  onCancel?: () => void;
  /** data-testid for the root. */
  testId?: string;
}

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, error, hint, children }: FieldProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && !error ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Parse the sample-data textarea. Returns the parsed record or a parse
 * error message; tolerant of an empty box (treated as `{}`).
 */
function parseSampleJson(raw: string): { data: Record<string, unknown> | null; error: string | null } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { data: {}, error: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { data: null, error: 'Sample data must be a JSON object.' };
    }
    return { data: parsed as Record<string, unknown>, error: null };
  } catch (err) {
    return { data: null, error: `Invalid JSON: ${(err as Error).message}` };
  }
}

export function CustomWidgetBuilder({
  initialDraft,
  onSave,
  onCancel,
  testId = 'custom-widget-builder',
}: CustomWidgetBuilderProps): React.ReactElement {
  const [draft, setDraft] = React.useState<CustomWidgetDraft>({
    ...DEFAULT_DRAFT,
    ...initialDraft,
  });
  const [sampleJson, setSampleJson] = React.useState(DEFAULT_SAMPLE_JSON);
  const [submitted, setSubmitted] = React.useState(false);

  const set = React.useCallback(
    <K extends keyof CustomWidgetDraft>(key: K, value: CustomWidgetDraft[K]) => {
      setDraft((d) => ({ ...d, [key]: value }));
    },
    [],
  );

  // Parse the sample data + derive the available-field allow-list from it so
  // the script validator can flag typos against the real shape.
  const sample = React.useMemo(() => parseSampleJson(sampleJson), [sampleJson]);
  const availableFields = React.useMemo(
    () => (sample.data ? Object.keys(sample.data) : undefined),
    [sample.data],
  );

  // Live script lint (independent of full submit) for the editor underline.
  const scriptLint = React.useMemo(
    () => validateScript(draft.valueScript, { allowedFields: availableFields }),
    [draft.valueScript, availableFields],
  );

  // Full build result — drives the error map + the Save gate.
  const build: BuildResult = React.useMemo(
    () => buildCustomWidget({ ...draft, availableFields }),
    [draft, availableFields],
  );

  // Backend-compatibility: does the (valid) script fall inside the backend
  // SimpleExpression sandbox subset, so it can persist + execute server-side?
  const backendCompat = React.useMemo(() => {
    if (!scriptLint.ok || draft.valueScript.trim().length === 0) return null;
    return classifyBackendCompatibility(draft.valueScript);
  }, [scriptLint.ok, draft.valueScript]);

  // Preview: evaluate the (valid) script against the sample data + format it.
  const preview = React.useMemo(() => {
    if (sample.error) return { ok: false as const, text: '—', error: sample.error };
    if (!scriptLint.ok) {
      return { ok: false as const, text: '—', error: scriptLint.issues[0]?.message ?? 'Invalid script.' };
    }
    const evaluated = evaluateScript(draft.valueScript, sample.data ?? {});
    if (!evaluated.ok) return { ok: false as const, text: '—', error: evaluated.error };
    return {
      ok: true as const,
      text: formatWidgetValue(evaluated.value, draft.format, draft.unit),
      error: null,
    };
  }, [sample, scriptLint, draft.valueScript, draft.format, draft.unit]);

  const errorFor = (key: keyof CustomWidgetDraft): string | undefined =>
    submitted ? build.errors[key] : undefined;

  const handleSave = React.useCallback(() => {
    setSubmitted(true);
    if (build.ok && build.descriptor && build.definition) {
      onSave?.({ descriptor: build.descriptor, definition: build.definition });
    }
  }, [build, onSave]);

  // On submit the full-build error wins; before submit we surface the live
  // lint issue inline (below the textarea) rather than as a field error.
  const scriptError = errorFor('valueScript');
  const positionalIssue: ValidationIssue | undefined = scriptLint.issues[0];

  return (
    <div data-testid={testId} className="grid gap-6 md:grid-cols-2">
      {/* ── Form column ─────────────────────────────────────────────── */}
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <Field label="Widget label" htmlFor="cwb-label" error={errorFor('label')}>
          <Input
            id="cwb-label"
            value={draft.label}
            placeholder="Net OEE"
            aria-invalid={Boolean(errorFor('label'))}
            onChange={(e) => set('label', e.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Module" htmlFor="cwb-module" error={errorFor('moduleId')}>
            <Input
              id="cwb-module"
              value={draft.moduleId}
              placeholder="tyndo.production"
              aria-invalid={Boolean(errorFor('moduleId'))}
              onChange={(e) => set('moduleId', e.target.value)}
            />
          </Field>
          <Field
            label="Read permission"
            htmlFor="cwb-perm"
            error={errorFor('readPermission')}
          >
            <Input
              id="cwb-perm"
              value={draft.readPermission}
              placeholder="production.read"
              aria-invalid={Boolean(errorFor('readPermission'))}
              onChange={(e) => set('readPermission', e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Data-source endpoint"
          htmlFor="cwb-endpoint"
          hint="GET endpoint the widget polls, e.g. /api/v1/production/oee/current"
          error={errorFor('endpointPath')}
        >
          <Input
            id="cwb-endpoint"
            value={draft.endpointPath}
            placeholder="/api/v1/..."
            aria-invalid={Boolean(errorFor('endpointPath'))}
            onChange={(e) => set('endpointPath', e.target.value)}
          />
        </Field>

        <Field
          label="Value expression"
          htmlFor="cwb-script"
          hint={
            availableFields && availableFields.length > 0
              ? `Fields: ${availableFields.join(', ')}`
              : 'Reference fields from the sample data, e.g. round(value, 1)'
          }
          error={scriptError}
        >
          <Textarea
            id="cwb-script"
            rows={3}
            value={draft.valueScript}
            placeholder="round(oee.availability * oee.performance * oee.quality, 1)"
            aria-invalid={Boolean(scriptError)}
            className="font-mono text-xs"
            onChange={(e) => set('valueScript', e.target.value)}
          />
          {!submitted && draft.valueScript.trim().length > 0 && !scriptLint.ok ? (
            <p className="text-xs text-destructive">
              {positionalIssue?.message}
              {positionalIssue && positionalIssue.position >= 0
                ? ` (at character ${positionalIssue.position + 1})`
                : ''}
            </p>
          ) : null}
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Format" htmlFor="cwb-format">
            <Select
              value={draft.format}
              onValueChange={(v) => set('format', v as CustomWidgetFormat)}
            >
              <SelectTrigger id="cwb-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Unit (optional)" htmlFor="cwb-unit">
            <Input
              id="cwb-unit"
              value={draft.unit ?? ''}
              placeholder="units/hr"
              onChange={(e) => set('unit', e.target.value)}
            />
          </Field>
          <Field label="Size" htmlFor="cwb-size">
            <Select
              value={draft.size ?? 'small'}
              onValueChange={(v) => set('size', v as WidgetSize)}
            >
              <SelectTrigger id="cwb-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SIZE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button type="submit" disabled={submitted && !build.ok}>
            Save widget
          </Button>
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      </form>

      {/* ── Preview column ──────────────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <Field
          label="Sample data (JSON)"
          htmlFor="cwb-sample"
          hint="Paste a representative response. The preview evaluates the expression against this — the live endpoint is wired by the shell."
        >
          <Textarea
            id="cwb-sample"
            rows={6}
            value={sampleJson}
            className="font-mono text-xs"
            aria-invalid={Boolean(sample.error)}
            onChange={(e) => setSampleJson(e.target.value)}
          />
        </Field>

        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">Preview</p>
          <WidgetCard
            descriptor={{
              widgetId: 'custom-widget-builder-preview',
              label: draft.label.trim() || 'Custom widget',
              moduleId: draft.moduleId,
            }}
            testId="custom-widget-preview"
          >
            <div className="flex min-h-[64px] items-center">
              {preview.ok ? (
                <span className="text-3xl font-semibold tabular-nums">{preview.text}</span>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {draft.valueScript.trim().length === 0
                    ? 'Write a value expression to preview.'
                    : preview.error}
                </span>
              )}
            </div>
          </WidgetCard>
        </div>

        {backendCompat ? (
          backendCompat.backendCompatible ? (
            <Alert variant="success" data-testid="cwb-backend-compat">
              <AlertTitle>Runs server-side</AlertTitle>
              <AlertDescription>
                This expression is within the backend SimpleExpression sandbox, so
                it is persisted and executed on the server when saved.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="warning" data-testid="cwb-backend-compat">
              <AlertTitle>Preview-only expression</AlertTitle>
              <AlertDescription>
                Uses constructs the backend sandbox does not support, so it runs
                only in this browser preview. To execute server-side, restrict it
                to numbers, field access, and the {`+ - * /`} operators.
                {backendCompat.reasons.length > 0 ? ` (${backendCompat.reasons[0]})` : ''}
              </AlertDescription>
            </Alert>
          )
        ) : null}

        <Alert variant="info">
          <AlertTitle>Sandboxed expressions</AlertTitle>
          <AlertDescription>
            Expressions run in a restricted evaluator — field access, arithmetic,
            comparisons, ternaries, and a fixed set of functions (round, abs, min,
            max, upper, lower, len, coalesce, concat). There is no access to the
            page, network, or globals.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
